/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { graphql } from 'gatsby';
import { type IGatsbyImageData } from 'gatsby-plugin-image';

import { BlogPostContent } from '../components/blog/BlogPostContent';
import { type RecommendationTypes } from '../components/use-cases/Recommendations';

export interface BlogPost {
  author: string;
  blogKicker?: {
    body: {
      body: string;
    };
    buttonLabel1?: string;
    buttonLabel2?: string;
    buttonUrl1?: string;
    buttonUrl2?: string;
  };
  body: {
    raw: string;
    references: {
      __typename: string;
      filter(arg0: (ref: any) => boolean): unknown;
    };
  };
  categories: Array<{ name: string }>;
  date: string;
  disableSideNav?: boolean;
  friendsOfData: boolean;
  friendsOfDataProduct: string;
  heroImage?: {
    gatsbyImageData: IGatsbyImageData;
    title: string;
    url: string;
  };
  heroImageCredit?: string;
  heroLoomVideo?: {
    url: string;
  };
  interview: boolean;
  recommendations: RecommendationTypes[];
  seoMetaDescription: string;
  seoTitle: string;
  showPhotoOverlay: boolean;
  slug: string;
  socialSharePreviewImage: {
    title: string;
    url: string;
  };
  synopsis?: string;
  title: string;
}

interface BlogPostTemplateProps {
  data: {
    contentfulBlogPost: BlogPost;
  };
}

const BlogPostTemplate: React.ComponentType<BlogPostTemplateProps> = React.memo(
  ({ data }) => {
    return <BlogPostContent post={data.contentfulBlogPost} />;
  }
);

export default BlogPostTemplate;

export const pageQuery = graphql`
  query ($slug: String!) {
    contentfulBlogPost(slug: { eq: $slug }) {
      title
      synopsis
      slug
      heroImage {
        title
        url
        gatsbyImageData(placeholder: BLURRED, width: 840)
      }
      heroImageCredit
      heroLoomVideo {
        url
      }
      showPhotoOverlay
      date
      friendsOfData
      disableSideNav
      friendsOfDataProduct
      interview
      author
      seoTitle
      seoMetaDescription
      socialSharePreviewImage {
        url
        title
      }
      categories {
        name
      }
      recommendations {
        ... on ContentfulUseCaseCategory {
          __typename
          slug
          title
          gridImage {
            url
            title
          }
          socialSharePreviewImage {
            url
            title
          }
          seoMetaDescription
        }
        ... on ContentfulUseCase {
          __typename
          slug
          category {
            slug
          }
          name
          author
          seoMetaDescription
          gridImage {
            url
            title
          }
        }
        ... on ContentfulBlogPost {
          __typename
          slug
          date
          title
          author
          seoMetaDescription
          heroImage {
            url
            title
          }
        }
      }
      blogKicker {
        body {
          body
        }
        buttonLabel1
        buttonUrl1
        buttonLabel2
        buttonUrl2
      }
      body {
        raw
        references {
          __typename
          ... on ContentfulBlogCallout {
            contentful_id
            body {
              raw
            }
          }
          ... on ContentfulBlogHint {
            contentful_id
            body {
              raw
            }
          }
          ... on ContentfulBlogQuote {
            contentful_id
            body {
              body
            }
          }
          ... on ContentfulBlogHexEmbed {
            contentful_id
            source
            height
          }
          ... on ContentfulBlogFloatImage {
            contentful_id
            image {
              url
              title
            }
            offset
            position
          }
          ... on ContentfulAsset {
            contentful_id
            url
            title
            mimeType
            description
            gatsbyImageData(placeholder: BLURRED, width: 740)
          }
          ... on ContentfulBlogLoomVideo {
            contentful_id
            url
          }
          ... on ContentfulBlogHtml {
            contentful_id
            markup {
              markup
            }
          }

          ... on ContentfulAudioSnippet {
            contentful_id
            audioFile {
              url
            }
            startTimeStamp
            endTimeStamp
          }
        }
      }
    }
  }
`;
